import React, { useState } from "react";
import { KTSVG } from "../../../helpers";

type Props = {
  className: string;
  innerPadding?: string;
};

const StepContent13: React.FC<Props> = ({ className, innerPadding = "" }) => {
  const [category, setCategory] = useState("1");
  const [option, setOption] = useState("1");
  const [fleet, setFleet] = useState("1");
  return (

      <div className={`card ${className}`}>
        {/* begin::Body */}
        <div className={`card-body ${innerPadding}`}>
          {/* begin::Title */}
          <div className="mb-10">
            <h3 className="fw-bold text-dark mb-2 fs-3">Map customer acquisition process</h3>
            <span className="fs-7 text-muted">
             Read each section and then fill in your answers at the bottom.
            </span>
          </div>
          {/* end::Title */}

          {/* begin::Section */}
          <div className="mb-10">
            <div className="accordion" id="kt_accordion_1">
              <div className="accordion-item">
                <h2 className="accordion-header" id="kt_accordion_1_header_1">
                  <button
                    className="accordion-button fs-7 fw-bold"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#kt_accordion_1_body_1"
                    aria-expanded="true"
                    aria-controls="kt_accordion_1_body_1"
                  >
                    Map customer acquisition process
                  </button>
                </h2>
                <div
                  id="kt_accordion_1_body_1"
                  className="accordion-collapse collapse show"
                  aria-labelledby="kt_accordion_1_header_1"
                  data-bs-parent="#kt_accordion_1"
                >
                  <div className="accordion-body">
                  In the previous step, we determined “who” needed to make the decision to buy our product. In this step, we will determine “how”. Specifically, in this step you will map out the process by which a customer decides to purchase your product.

In the process of doing this, we will identify the key variables, understand what drives those, and also estimate a very important variable going forward: The length of the sales cycle for your product. You will also identify any budgetary, regulatory, or compliance hurdles that would slow your ability to sell the product. This is also known as mapping the DMP (Decision Making Process), but this description is less precise than the current title, the Customer Aqcuisition Process (CAP).

By creating a map of the process to acquire a paying customer and identifying the fundamental drivers of the length of the sales cycle, you will build the foundation for the Cost of Customer Acquisition calculation.

Key benefits from this exercise will include identifying hidden obstacles that will inhibit your ability to sell your product and get paid, and also to be able to show your potential lenders and/or investors that you understand the customer’s buying process, which for many is a prerequisite to investing in your business.
                  </div>
                </div>
              </div>
              <div className="accordion-item">
                <h2 className="accordion-header" id="kt_accordion_1_header_2">
                  <button
                    className="accordion-button fs-7 fw-bold collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#kt_accordion_1_body_2"
                    aria-expanded="false"
                    aria-controls="kt_accordion_1_body_2"
                  >
                    Mapping the process
                  </button>
                </h2>
                <div
                  id="kt_accordion_1_body_2"
                  className="accordion-collapse collapse show"
                  aria-labelledby="kt_accordion_1_header_2"
                  data-bs-parent="#kt_accordion_1"
                >
                  <div className="accordion-body">
                  By mapping the process to acquire a paying customer, you will capture more detail about each of these items especially now considering the DMU, as well as map out the internal purchasing mechanisms of your target customer.

Create a table or diagram with the following items from the Full Life Cycle Use Case (Step 6), which will be the backbone of your customer acquisition process:

How customers will determine they have a need and/or opportunity to move away from their status quo and how to activate customers to feel they have to do something different, i.e., purchase your product;
How customers will find out about your product;
How customers will analyze your product;
How customers will acquire your product;
How customers will install your product;
How customers will pay for your product.
                  </div>
                </div>
              </div>
              <div className="accordion-item">
                <h2 className="accordion-header" id="kt_accordion_1_header_3">
                  <button
                    className="accordion-button fs-7 fw-bold collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#kt_accordion_1_body_3"
                    aria-expanded="false"
                    aria-controls="kt_accordion_1_body_3"
                  >
                    Detail the process map
                  </button>
                </h2>
                <div
                  id="kt_accordion_1_body_3"
                  className="accordion-collapse collapse"
                  aria-labelledby="kt_accordion_1_header_3"
                  data-bs-parent="#kt_accordion_1"
                >
                  <div className="accordion-body">
                  For each step in the process, add the following information:

Who are the key players from the DMU that will be involved in this step? What is their influence on the step or overall process?
How long will it take to complete this step? Put it in temporal sequence noting any that can run in parallel. Are there any governmental or quasi-governmental regulations that impact this step?
What are the inputs and outputs of this step?
                  </div>
                </div>
              </div>
              <div className="accordion-item">
                <h2 className="accordion-header" id="kt_accordion_1_header_4">
                  <button
                    className="accordion-button fs-7 fw-bold collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#kt_accordion_1_body_4"
                    aria-expanded="false"
                    aria-controls="kt_accordion_1_body_4"
                  >
                    Detail budgetary factors
                  </button>
                </h2>
                <div
                  id="kt_accordion_1_body_4"
                  className="accordion-collapse collapse"
                  aria-labelledby="kt_accordion_1_header_4"
                  data-bs-parent="#kt_accordion_1"
                >
                  <div className="accordion-body">
                  A key factor in each step is to identify the budgeting/purchasing authority and levels of each individual involved in that component of the process as appropriate. One common limit you will find is that an individual can only purchase items up to a certain dollar amount, such as $5,000, without approval from a more senior person.

Sometimes, this approval comes directly from one decision maker, while at other times, it kicks off a long and involved process with the purchasing department and its regulations.

Identify budgeting/purchasing limits for each key player in the DMU involved in the process.
Identify the approval chain.
Understand the budgeting process.
Understand whether payment will come from the yearly operating budget or the longer-term capital budget, and even the specific budget.
Example

Here is one example that could be helpful but use a format that is appropriate for your situation and do not feel constrained by this example. Note that while this is a good example overall, it does not complete the cycle and clearly document how long it takes to get paid. You need to understand how the cash will flow to your new venture because cash is like oxygen to your new organization. Without it, you die.

MWFS DMP Example
                  </div>
                </div>
              </div>

            </div>
          </div>
          {/* end::Section */}

        </div>
        {/* end: Card Body */}
      </div>

  );
};

export { StepContent13 };
